import { ProductDto, OnlineClass, ProductAccessType } from '../types/api';
import emploApiClient from '../lib/api/emploApiClient';

class OnlineClassServiceError extends Error {
    public statusCode: number;
    
    constructor(message: string, statusCode: number = 500) {
        super(message);
        this.name = 'OnlineClassServiceError';
        this.statusCode = statusCode;
    }
}

// Interface for the JSON.NET serialization response format
interface JsonNetResponse<T> {
    $id: string;
    $values: T[];
}

// Helper function to convert string accessType to enum
const convertAccessType = (accessType: string): ProductAccessType => {
    switch (accessType.toLowerCase()) {
        case 'free':
            return ProductAccessType.Free;
        case 'paid':
            return ProductAccessType.Paid;
        case 'subscription':
            return ProductAccessType.Subscription;
        default:
            console.warn(`Unknown access type: ${accessType}, defaulting to Free`);
            return ProductAccessType.Free;
    }
};

// Transform ProductDto to OnlineClass interface
const transformProductToOnlineClass = (product: ProductDto): OnlineClass => {
    // Extract schedule data
    let scheduleData: any[] = [];
    if (product.schedule) {
        if (Array.isArray(product.schedule)) {
            scheduleData = product.schedule;
        } else if (product.schedule.$values) {
            scheduleData = product.schedule.$values;
        }
    }

    // Use the first schedule entry for startDate, or fallback to product.startDate
    let startDate = new Date();
    let endDate: Date | undefined = undefined;

    if (scheduleData.length > 0) {
        const firstSchedule = scheduleData[0];
        // Combine date and startTime to create a proper datetime
        const scheduleDate = new Date(firstSchedule.date);
        const [hours, minutes] = firstSchedule.startTime.split(':').map(Number);
        scheduleDate.setHours(hours, minutes, 0, 0);
        startDate = scheduleDate;

        // Create end date from date and endTime
        const scheduleEndDate = new Date(firstSchedule.date);
        const [endHours, endMinutes] = firstSchedule.endTime.split(':').map(Number);
        scheduleEndDate.setHours(endHours, endMinutes, 0, 0);
        endDate = scheduleEndDate;
    } else if (product.startDate) {
        startDate = new Date(product.startDate);
        endDate = product.endDate ? new Date(product.endDate) : undefined;
    }

    return {
        id: product.id,
        title: product.title,
        description: product.descriptionShort,
        shortDescription: product.descriptionShort,
        imageUrl: product.imageUrl,
        startDate: startDate,
        endDate: endDate,
        price: product.price,
        currency: product.currency,
        maxStudents: product.maxStudents,
        currentEnrollment: product.currentEnrollment,
        instructorName: product.instructorName,
        meetingLink: product.meetingLink || '',
        isFeatured: product.isFeatured,
        averageRating: product.averageRating,
        ratingCount: product.ratingCount,
        minimumAge: product.minimumAge,
        maximumAge: product.maximumAge,
        accessType: convertAccessType(product.accessType),
        categoryName: product.categoryName || '',
        hasSchedule: product.hasSchedule,
        schedule: scheduleData,
        viewCount: product.viewCount,
        createdDate: new Date(product.createdDate),
        lastUpdated: product.lastUpdated ? new Date(product.lastUpdated) : undefined,
        whatyourwilllearn: product.whatyourwilllearn
    };
};

export const onlineClassService = {
    async getOnlineClasses(): Promise<OnlineClass[]> {
        try {
            console.log('OnlineClassService: Fetching online classes from EmploAPI');
            const response = await emploApiClient.get<JsonNetResponse<ProductDto> | ProductDto[]>('/api/v1/PublicProducts/online-classes');
            console.log('OnlineClassService: Raw API response:', response.data);
            
            // Handle both JSON.NET serialization format and direct array format
            let productDtos: ProductDto[];
            if (response.data && typeof response.data === 'object' && '$values' in response.data) {
                // JSON.NET serialization format
                productDtos = (response.data as JsonNetResponse<ProductDto>).$values;
                console.log('OnlineClassService: Extracted from $values:', productDtos);
            } else if (Array.isArray(response.data)) {
                // Direct array format
                productDtos = response.data as ProductDto[];
                console.log('OnlineClassService: Direct array format:', productDtos);
            } else {
                console.error('OnlineClassService: Unexpected response format:', response.data);
                throw new Error('Unexpected API response format');
            }
            
            const onlineClasses = productDtos.map(transformProductToOnlineClass);
            console.log('OnlineClassService: Transformed online classes:', onlineClasses);
            
            return onlineClasses;
        } catch (error: any) {
            console.error('OnlineClassService: Error fetching online classes:', error);
            throw new OnlineClassServiceError(
                error.response?.data?.message || 'Failed to fetch online classes',
                error.response?.status || 500
            );
        }
    }
};

export { OnlineClassServiceError }; 